import {
  useGetDashboardSummary,
  useListProperties,
  type Permission,
  type PropertyListOut,
} from '@myrentalmate/api-client';
import { Alert, Box, Button, Chip, Link as MuiLink, Paper, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { Link } from 'react-router';

import { DataTable, type DataColumn } from '../../components/DataTable';
import { OccupancyMeter, StatRow, StatTile } from '../../components/Stats';
import { okData, okItems } from '../../lib/apiData';
import { formatDate, formatMoney, formatOccupancy } from '../../lib/format';
import { activeOrgName, useMe } from '../../lib/me';
import { attentionItems } from './attention';

const QUICK_ACTIONS: { label: string; to: string; permission: Permission }[] = [
  { label: 'Add property', to: '/staff/properties?add=1', permission: 'properties.manage' },
  { label: 'Add lease', to: '/staff/leases?add=1', permission: 'leases.manage' },
  { label: 'Add resident', to: '/staff/residents?add=1', permission: 'residents.manage' },
  { label: 'Add vendor', to: '/staff/vendors?add=1', permission: 'vendors.manage' },
  { label: 'Upload a file', to: '/staff/files', permission: 'properties.manage' },
];

const PROPERTY_COLUMNS: DataColumn<PropertyListOut>[] = [
  {
    label: 'Property',
    render: (p) => (
      <MuiLink component={Link} to={`/staff/properties/${p.id}`}>
        {p.name}
      </MuiLink>
    ),
  },
  { label: 'Units', render: (p) => p.stats.units },
  {
    label: 'Occupancy',
    render: (p) => (
      <OccupancyMeter occupied={p.stats.occupied_units} vacant={p.stats.vacant_units} />
    ),
  },
  { label: 'Vacant', render: (p) => p.stats.vacant_units },
  { label: 'Market rent / mo', render: (p) => formatMoney(p.stats.market_rent) },
];

function Panel({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 2, flex: '1 1 340px', minWidth: 0 }}>
      <Stack direction="row" sx={{ alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {title}
        </Typography>
        {action}
      </Stack>
      {children}
    </Paper>
  );
}

export function StaffDashboard() {
  const { data: me } = useMe();
  const { data, isError } = useGetDashboardSummary();
  const properties = useListProperties({ page_size: 5 });
  const summary = okData(data);
  const attention = summary ? attentionItems(summary) : [];
  const actions = QUICK_ACTIONS.filter((a) => me?.permissions.includes(a.permission));
  const propertyRows = okItems(properties.data);
  const propertyTotal = okData(properties.data)?.total ?? 0;
  const canReadLeases = me?.permissions.includes('leases.read') ?? false;

  return (
    <Stack spacing={3}>
      <Stack direction="row" useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          {(me && activeOrgName(me)) ?? 'Dashboard'}
        </Typography>
        {actions.map((a) => (
          <Button key={a.label} variant="outlined" size="small" component={Link} to={a.to}>
            {a.label}
          </Button>
        ))}
      </Stack>
      {isError && <Alert severity="error">Couldn't load your numbers. Please try again.</Alert>}

      <StatRow>
        <StatTile
          label="Occupancy"
          value={formatOccupancy(summary?.occupied_units, summary?.vacant_units)}
          hint={
            summary &&
            `${summary.occupied_units} of ${summary.occupied_units + summary.vacant_units} rentable units`
          }
          to="/staff/properties"
        />
        <StatTile label="Vacant Units" value={summary?.vacant_units} to="/staff/properties" />
        {canReadLeases && (
          <>
            <StatTile
              label="Charged This Month"
              value={summary && formatMoney(summary.charged_this_month)}
              to="/staff/leases"
            />
            <StatTile
              label="Collected This Month"
              value={summary && formatMoney(summary.collected_this_month)}
              to="/staff/payments"
            />
            <StatTile
              label="Owed by Residents"
              value={summary && formatMoney(summary.amount_owed)}
              hint={summary && `${summary.leases_owing} of ${summary.active_leases} leases`}
              to="/staff/leases"
            />
            <StatTile label="Deposits Held" value={summary && formatMoney(summary.deposits_held)} />
          </>
        )}
        {!canReadLeases && (
          <>
            <StatTile
              label="Market Rent, Occupied"
              value={summary && formatMoney(summary.occupied_market_rent)}
              hint="Per month"
            />
            <StatTile
              label="Market Rent, Vacant"
              value={summary && formatMoney(summary.vacant_market_rent)}
              hint="Per month not being earned"
            />
          </>
        )}
        <StatTile label="Residents" value={summary?.residents} to="/staff/residents" />
        <StatTile label="Vendors" value={summary?.vendors} to="/staff/vendors" />
      </StatRow>

      {summary?.properties === 0 ? (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Start with Your First Property
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Add a property, then its units and residents.
          </Typography>
          <Button variant="contained" component={Link} to="/staff/properties?add=1">
            Add a property
          </Button>
        </Paper>
      ) : (
        <>
          <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 2 }}>
            <Panel title="Needs Attention">
              {attention.length === 0 ? (
                <Typography color="text.secondary">Nothing needs attention right now.</Typography>
              ) : (
                <Stack spacing={1}>
                  {attention.map((item) => (
                    <Stack
                      key={item.text}
                      direction="row"
                      spacing={1}
                      sx={{ alignItems: 'center' }}
                    >
                      <Chip
                        size="small"
                        label={item.severity === 'urgent' ? 'Urgent' : 'Review'}
                        color={item.severity === 'urgent' ? 'error' : 'warning'}
                      />
                      <MuiLink component={Link} to={item.to}>
                        {item.text}
                      </MuiLink>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Panel>
            {canReadLeases && (
              <Panel title="Who Owes Money">
                {summary?.owing_leases.length === 0 ? (
                  <Typography color="text.secondary">Nobody owes anything right now.</Typography>
                ) : (
                  <Stack spacing={1}>
                    {summary?.owing_leases.map((lease) => (
                      <Stack
                        key={lease.lease_id}
                        direction="row"
                        sx={{ justifyContent: 'space-between', gap: 2 }}
                      >
                        <MuiLink component={Link} to={`/staff/leases/${lease.lease_id}`}>
                          {lease.label}
                        </MuiLink>
                        <Typography>{formatMoney(lease.balance)}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Panel>
            )}
            {canReadLeases && (summary?.ending_leases.length ?? 0) > 0 && (
              <Panel title="Leases Ending Soon">
                <Stack spacing={1}>
                  {summary?.ending_leases.map((lease) => (
                    <Stack
                      key={lease.lease_id}
                      direction="row"
                      sx={{ justifyContent: 'space-between', gap: 2 }}
                    >
                      <MuiLink component={Link} to={`/staff/leases/${lease.lease_id}`}>
                        {lease.label}
                      </MuiLink>
                      <Typography color="text.secondary">{formatDate(lease.end_date)}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Panel>
            )}
            <Panel title="Vacant Units">
              {summary?.vacant_unit_list.length === 0 ? (
                <Typography color="text.secondary">
                  {summary?.units === 0 ? 'No units yet.' : 'Every rentable unit is occupied.'}
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {summary?.vacant_unit_list.map((u) => (
                    <Stack
                      key={`${u.property_id}-${u.unit_number}`}
                      direction="row"
                      sx={{ justifyContent: 'space-between', gap: 2 }}
                    >
                      <MuiLink component={Link} to={`/staff/properties/${u.property_id}`}>
                        {u.property_name} · {u.unit_number}
                      </MuiLink>
                      <Typography color="text.secondary">{formatMoney(u.market_rent)}</Typography>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Panel>
          </Stack>

          <Box>
            <Panel
              title="Properties"
              action={
                propertyTotal > propertyRows.length && (
                  <Button size="small" component={Link} to="/staff/properties">
                    See all {propertyTotal}
                  </Button>
                )
              }
            >
              <DataTable columns={PROPERTY_COLUMNS} rows={propertyRows} rowKey={(p) => p.id} />
            </Panel>
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary">
              Market rent is the asking rent set on each unit. Charged and collected come from your
              leases and the ledger.
            </Typography>
          </Box>
        </>
      )}
    </Stack>
  );
}
